import { useState, useCallback, useEffect } from 'react';
import {
  COUNTRIES,
  SUPPORTED_COUNTRY_CODES,
  resolveCountry,
  storeCountry,
  setCountryInUrl,
  getCountryFromUrl,
  hayEleccionPrevia,
  describirDeteccion,
  DEFAULT_COUNTRY,
} from '../utils/countryUtils';

/**
 * País activo de la sesión.
 *
 * Se resuelve por prioridad: lo que dice la URL, después lo que el usuario
 * eligió antes, y recién al final la detección por zona horaria. El país queda
 * reflejado en la URL (`?pais=ar`) para que se vea de un vistazo en qué
 * catálogo se está navegando y para que un enlace compartido abra donde
 * corresponde.
 *
 * Devuelve:
 *   country      – código actual ('ES' | 'AR')
 *   countryMeta  – configuración completa desde COUNTRIES
 *   setCountry   – cambia el país, lo persiste y lo refleja en la URL
 *   countries    – todos los países soportados
 */
export const useCountry = () => {
  const [country, setCountryState] = useState(() => resolveCountry());

  // ¿Hay que preguntarle en qué catálogo quiere estar?
  //
  // Se calcula UNA vez, al montar, y a propósito: si se recalculara, elegir un
  // país haría desaparecer el diálogo por efecto colateral en vez de por una
  // decisión explícita, y cualquier cambio posterior desde el encabezado
  // volvería a evaluar la condición.
  //
  // La regla es no molestar a quien ya contestó: sólo se pregunta cuando no hay
  // nada guardado ni en el enlace. Quien ya eligió tiene el selector del
  // encabezado, que es donde uno lo busca.
  const [faltaElegir, setFaltaElegir] = useState(() => !hayEleccionPrevia());

  // ¿El país vino escrito en el enlace?
  //
  // Se mira UNA vez, en el primer render, y antes de que el efecto de abajo
  // escriba `?pais=` por su cuenta: después ya no se distingue el país que traía
  // el enlace del que puso la propia página.
  //
  // Importa porque `resolveCountry()` da prioridad al enlace sobre lo guardado
  // —«un enlace compartido debe abrirse en el país que dice el enlace»— y esa
  // prioridad se perdía un instante más tarde: al resolver la sesión,
  // `adoptarPreferencia` imponía el país de la cuenta y reescribía la URL.
  // Compartir un enlace de España con alguien que tenía Argentina guardada le
  // abría Argentina.
  const [paisDelEnlace] = useState(() => Boolean(getCountryFromUrl()));
  const [deteccion] = useState(() => describirDeteccion());

  // Deja la URL en su estado canónico desde el primer render: si se entró sin
  // parámetro, se agrega el detectado; así la dirección siempre dice el país.
  useEffect(() => {
    setCountryInUrl(country);
  }, [country]);

  // El botón atrás puede devolver a una URL con otro país: hay que seguirla,
  // porque si no la barra de direcciones y el contenido dejan de coincidir.
  useEffect(() => {
    const alNavegar = () => {
      const enUrl = getCountryFromUrl();
      if (enUrl && enUrl !== country) {
        storeCountry(enUrl);
        setCountryState(enUrl);
      }
    };
    window.addEventListener('popstate', alNavegar);
    return () => window.removeEventListener('popstate', alNavegar);
  }, [country]);

  const setCountry = useCallback((code) => {
    if (!SUPPORTED_COUNTRY_CODES.includes(code)) return;
    storeCountry(code);
    setCountryInUrl(code);
    setCountryState(code);
  }, []);

  // Elegir desde el diálogo de la primera visita: además de fijar el país,
  // cierra la pregunta para siempre.
  const elegirPaisInicial = useCallback((code) => {
    setCountry(code);
    setFaltaElegir(false);
  }, [setCountry]);

  // Para quien ya lo tiene decidido en su cuenta: se adopta esa preferencia sin
  // preguntar nada. Es la misma idea —no molestar con algo ya contestado— pero
  // la respuesta viene del servidor y llega unos milisegundos más tarde.
  //
  // No depende de `country` a propósito. Cuando dependía, la función cambiaba de
  // identidad en cada cambio de país, el efecto de App.js que la llama volvía a
  // dispararse y reimponía el país del perfil: elegir España desde el encabezado
  // rebotaba a Argentina al instante. Quién decide cuándo adoptar es de App.js;
  // acá sólo se aplica.
  const adoptarPreferencia = useCallback((code) => {
    if (!SUPPORTED_COUNTRY_CODES.includes(code)) return;
    // No hay nada que preguntar en cualquier caso: la cuenta ya lo dice.
    setFaltaElegir(false);
    // Pero si el enlace nombraba un país, ése gana: es una intención más
    // reciente y más concreta que una preferencia guardada hace meses.
    if (paisDelEnlace) return;
    setCountry(code);
  }, [paisDelEnlace, setCountry]);

  return {
    country,
    countryMeta: COUNTRIES[country] || COUNTRIES[DEFAULT_COUNTRY],
    setCountry,
    countries: Object.values(COUNTRIES),
    faltaElegir,
    deteccion,
    elegirPaisInicial,
    adoptarPreferencia,
  };
};
